import { createContext, use, useSyncExternalStore } from "react";
import type { JSX, ReactNode } from "react";
import type { InstallController, InstallOutcome, InstallState } from "./installPrompt";
import type { PlatformKind } from "./platform";

const InstallContext = createContext<InstallController | null>(null);

/** Properties of {@link InstallProvider}. */
export interface InstallProviderProps {
  readonly controller: InstallController;
  readonly children: ReactNode;
}

/**
 * Provides the installation controller.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function InstallProvider(props: InstallProviderProps): JSX.Element {
  const provider = <InstallContext value={props.controller}>{props.children}</InstallContext>;

  return provider;
}

/** Installation state and action for components. */
export interface Installation {
  readonly platform: PlatformKind;
  readonly state: InstallState;
  readonly install: () => Promise<InstallOutcome>;
}

/**
 * Returns the installation state and re-renders on changes.
 *
 * @returns State, platform and install action.
 * @throws Error if no {@link InstallProvider} is present.
 */
export function useInstallation(): Installation {
  const controller = use(InstallContext);

  if (controller === null) {
    throw new Error("useInstallation must be used inside an InstallProvider.");
  }

  const state = useSyncExternalStore(controller.subscribe, controller.getState);
  const installation: Installation = {
    platform: controller.platform,
    state,
    install: controller.install,
  };

  return installation;
}
